import { Suspense, useContext, useEffect, useMemo } from "react";
import { useMutation, usePaginationFragment, usePreloadedQuery, useQueryLoader } from "react-relay";
import { useDebounceFilters } from "@hooks/useDebounceFilters";
import { useTranslation } from "@i18n/hooks";
import { type offerFilesTable_DeleteMutation } from "@relay/offerFilesTable_DeleteMutation.graphql";
import { type offerFilesTable_FilesListFragment$key } from "@relay/offerFilesTable_FilesListFragment.graphql";
import { type offerFilesTable_Query } from "@relay/offerFilesTable_Query.graphql";
import { type OfferFilesTable_Refetch } from "@relay/OfferFilesTable_Refetch.graphql";
import { DELETE_MUTATION, OFFER_FILES_FRAGMENT, QUERY } from "./offer-files-table.graphql";
import {
	type OfferFilesTableProps,
	type OfferFilesTableWithPreloadedQueryProps,
} from "./offer-files-table.types";
import { OfferUploadAllowedExtensions } from "../../files/file-filters/file-filters.const";
import { FilesTableUI } from "../../files/files-table/files-table-ui.component";
import {
	FileSearchContainer,
	type FilesFilters,
	FilesSearchContext,
} from "../../files/FileSearchContainer";
import { OfferFileDeleteDialog } from "../OfferFileDeleteDialog";

const OfferFilesTableComponent = ({
	selectionSettings,
	canUploadFiles,
	canDeleteFiles,
	queryRef,
}: OfferFilesTableWithPreloadedQueryProps) => {
	const { filters } = useContext(FilesSearchContext);

	const data = usePreloadedQuery<offerFilesTable_Query>(QUERY, queryRef);

	const {
		data: { Admin },
		hasPrevious,
		hasNext,
		loadPrevious,
		loadNext,
		refetch,
	} = usePaginationFragment<OfferFilesTable_Refetch, offerFilesTable_FilesListFragment$key>(
		OFFER_FILES_FRAGMENT,
		data,
	);

	const filtersWithDefault = useMemo(() => {
		const filtersCopy = { ...filters };
		filtersCopy.filterByFileTypes = filters.filterByFileTypes?.length
			? filters.filterByFileTypes
			: selectionSettings?.fileTypeOptions?.map((type) => type.value);
		return filtersCopy;
	}, [filters]);

	useDebounceFilters<FilesFilters>(filtersWithDefault, refetch);

	const [deleteOfferFile] = useMutation<offerFilesTable_DeleteMutation>(DELETE_MUTATION);

	const files = Admin.Offer.OfferFiles.edges;
	const connectionId = Admin.Offer.OfferFiles.__id;

	return (
		<FilesTableUI
			files={files}
			hasPrevious={hasPrevious}
			hasNext={hasNext}
			loadPrevious={loadPrevious}
			loadNext={loadNext}
			onRefetch={() => refetch({}, { fetchPolicy: "network-only" })}
			selectionSettings={selectionSettings}
			canUploadFiles={canUploadFiles}
			canDeleteFiles={canDeleteFiles}
			uploadEndpoint="/api/upload/offer"
			allowedUploadExtensions={OfferUploadAllowedExtensions}
			renderDeleteDialog={(fileId, onClose) => (
				<OfferFileDeleteDialog
					fileId={fileId}
					connectionId={connectionId}
					onClose={onClose}
					onConfirm={() => {
						deleteOfferFile({
							variables: {
								input: { ids: [fileId] },
								connections: [connectionId],
							},
							onCompleted: () => {
								onClose();
								refetch({}, { fetchPolicy: "network-only" });
							},
						});
					}}
				/>
			)}
		/>
	);
};

export const OfferFilesTable = ({
	selectionSettings,
	filterByFileTypes,
	...props
}: OfferFilesTableProps) => {
	const { t } = useTranslation("common");
	const [queryReference, loadQuery] = useQueryLoader<offerFilesTable_Query>(QUERY);

	useEffect(() => {
		loadQuery(
			{
				filterByFileTypes:
					selectionSettings?.fileTypeOptions?.map((o) => o.value) ?? filterByFileTypes,
			},
			{ fetchPolicy: "network-only" },
		);
	}, [loadQuery]);

	return (
		<Suspense fallback={t("app.loading")}>
			{queryReference && (
				<FileSearchContainer>
					<OfferFilesTableComponent
						{...props}
						selectionSettings={selectionSettings}
						filterByFileTypes={filterByFileTypes}
						queryRef={queryReference}
					/>
				</FileSearchContainer>
			)}
		</Suspense>
	);
};
